import { Box, Portal, Stack, Transition } from '@mantine/core';
import { useMemo } from 'react';
import { ToastAlert } from '../providers/alert-types';
import { useReducedMotion } from '../../animations/hooks';
import ToastItem from './ToastItem';

export interface ToastContainerProps {
  toasts: ToastAlert[];
  maxVisible?: number;
  onDismiss: (id: string) => void;
}

function ToastContainer({ toasts, maxVisible = 5, onDismiss }: ToastContainerProps) {
  const reducedMotion = useReducedMotion();

  // Group toasts by position
  const groupedToasts = useMemo(() => {
    return {
      'top-right': toasts.filter((t) => t.position === 'top-right'),
      'top-left': toasts.filter((t) => t.position === 'top-left'),
      'top-center': toasts.filter((t) => t.position === 'top-center'),
      'bottom-right': toasts.filter((t) => t.position === 'bottom-right'),
      'bottom-left': toasts.filter((t) => t.position === 'bottom-left'),
      'bottom-center': toasts.filter((t) => t.position === 'bottom-center'),
    };
  }, [toasts]);

  const getPositionStyles = (position: string): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      pointerEvents: 'none',
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: 16, right: 16 };
      case 'top-left':
        return { ...baseStyles, top: 16, left: 16 };
      case 'top-center':
        return { ...baseStyles, top: 16, left: '50%', transform: 'translateX(-50%)' };
      case 'bottom-right':
        return { ...baseStyles, bottom: 16, right: 16 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 16, left: 16 };
      case 'bottom-center':
        return { ...baseStyles, bottom: 16, left: '50%', transform: 'translateX(-50%)' };
      default:
        return { ...baseStyles, top: 16, right: 16 };
    }
  };

  const getTransition = (position: string) => {
    if (position.includes('left')) {
      return 'slide-right';
    }
    if (position.includes('right')) {
      return 'slide-left';
    }
    if (position.includes('top') || position.includes('bottom')) {
      return 'slide-down';
    }
    return 'slide-left';
  };

  return (
    <>
      {Object.entries(groupedToasts).map(([position, positionToasts]) => {
        if (positionToasts.length === 0) return null;

        return (
          <Portal key={position}>
            <Box style={getPositionStyles(position)}>
              <Stack gap="xs" style={{ pointerEvents: 'auto' }}>
                {positionToasts.slice(0, maxVisible).map((toast) => (
                  <Transition
                    key={toast.id}
                    mounted={true}
                    transition={getTransition(position)}
                    duration={reducedMotion ? 0 : 300}
                    timingFunction="ease-out"
                  >
                    {(styles) => (
                      <div style={styles}>
                        <ToastItem toast={toast} onDismiss={() => onDismiss(toast.id)} />
                      </div>
                    )}
                  </Transition>
                ))}
              </Stack>
            </Box>
          </Portal>
        );
      })}
    </>
  );
}

export default ToastContainer;
